var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { createContext, useContext, useEffect } from 'react';
import { Router, Route, Switch } from 'react-router';
import * as Oidc from 'oidc-client';
import AuthService from '../AuthService';
import usePrivateReducer from './reducer';
import { setLoggedInListeners } from './listeners';
import { ConfigurationState } from '../types/ConfigurationState';
import { Login, Main, OidcCallback, OidcSilentCallback, Redirector, TokenRefresher } from './';
export const PrivateContext = createContext({ privateState: {}, privateDispatch: null });
export const PublicContext = createContext(null);
export const useIdentity = () => useContext(PublicContext);
let forbiddenHandler = ({ reason }) => __awaiter(this, void 0, void 0, function* () { });
let logout = ({ message, redirect = false }) => __awaiter(this, void 0, void 0, function* () { });
let recordUserActivity = () => { };
function configure({ mode, props, privateDispatch }) {
    privateDispatch({
        type: 'CONFIGURING',
        debugMode: props.debugMode,
        legacyMode: (mode === 'legacy2019'),
        renderChildrenAfterLogout: props.renderChildrenAfterLogout,
        postAuthCallback: props.postAuthCallback
    });
    const authService = new AuthService();
    authService.init({
        authority: props.authority,
        client_id: props.client_id,
        mountpoint: props.mountpoint,
        scope: props.scopes.join(' ')
    });
    authService.userManager.events.addUserSignedOut(() => __awaiter(this, void 0, void 0, function* () {
        console.log('User logged out from OP, initiating local signout');
        localStorage.setItem('isLoggedIn', 'false');
        privateDispatch({ type: 'LOGOUT', message: 'Logout detected from another tab or application' });
        yield authService.userManager.removeUser();
    }));
    privateDispatch({ type: 'CONFIGURE_AUTH_SERVICE', authService });
    setLoggedInListeners({ authService, mode, privateDispatch });
    buildHandlers({ authService, mode, privateDispatch });
    if (props.debugMode) {
        Oidc.Log.logger = console;
        Oidc.Log.level = Oidc.Log.DEBUG;
    }
    privateDispatch({ type: 'CONFIGURED' });
}
function buildHandlers({ authService, mode, privateDispatch }) {
    forbiddenHandler = ({ reason }) => __awaiter(this, void 0, void 0, function* () {
        privateDispatch({ type: 'FORBIDDEN', reason });
        if (localStorage.getItem('isLoggedIn') === 'true') {
            console.warn('forbidden() called, logging user out');
            localStorage.setItem('isLoggedIn', 'false');
            if (mode === 'legacy2019')
                setTimeout(() => privateDispatch({ type: 'SHOW_LOGIN_MODAL' }), 1500);
            if (mode === 'identityServer')
                yield authService.signOutSilent({ privateDispatch });
        }
    });
    logout = ({ message, redirect = false }) => __awaiter(this, void 0, void 0, function* () {
        console.log(`logout() called with${redirect ? '' : 'out'} redirect, message: ${message}`);
        if (mode === 'legacy2019') {
            localStorage.setItem('isLoggedIn', 'false');
            privateDispatch({ type: 'LOGOUT', message });
            if (redirect) {
                console.log('redirecting to /auth/logout to finish legacy logout');
                privateDispatch({ type: 'BEGIN_REDIRECT', redirectTarget: '/auth/logout' });
            }
            return;
        }
        try {
            if (redirect && (yield authService.isLoggedIn())) {
                privateDispatch({ type: 'BEGIN_REDIRECT' });
                yield authService.signOut();
            }
            else {
                yield authService.signOutSilent({ privateDispatch });
            }
        }
        catch (error) {
            privateDispatch({ type: 'FATAL_ERROR', error, reason: 'Error while attempting to logout' });
        }
        finally {
            localStorage.setItem('isLoggedIn', 'false');
        }
    });
    recordUserActivity = () => {
        privateDispatch({ type: 'RECORD_USER_ACTIVITY' });
    };
}
const IdentityProviderAuthenticated = React.memo(({ authenticated, children, user }) => {
    const context = {
        isAuthenticated: authenticated,
        user,
        logout,
        forbiddenHandler,
        recordUserActivity
    };
    return (React.createElement(PublicContext.Provider, { value: context }, children));
});
export default function IdentityProvider({ children, history, authority, debugMode, client_id, mountpoint, postAuthCallback, scopes = [], mode = 'identityServer', refreshTokenAfter = 100, renderChildrenAfterLogout = false, loadingIndicator = undefined, fatalErrorFunc = undefined }) {
    let [privateState, privateDispatch] = usePrivateReducer();
    const { configurationState, fatalError, fatalReason, renderChildren } = privateState;
    useEffect(() => {
        configure({
            mode,
            privateDispatch,
            privateState,
            props: { authority, client_id, debugMode, mode, mountpoint, postAuthCallback, refreshTokenAfter, renderChildrenAfterLogout, scopes },
        });
    }, []);
    if (fatalError) {
        if (fatalErrorFunc) {
            return fatalErrorFunc(fatalReason, fatalError);
        }
        console.error(`Fatal Error: ${fatalReason}, ${fatalError}`);
        return null;
    }
    if (configurationState === ConfigurationState.Configured) {
        return (React.createElement(React.Fragment, null,
            !renderChildren && loadingIndicator,
            React.createElement(PrivateContext.Provider, { value: { privateState, privateDispatch } },
                React.createElement(Redirector, null),
                React.createElement(TokenRefresher, { refreshTokenAfterSeconds: refreshTokenAfter }),
                React.createElement(Router, { history: history },
                    React.createElement(Switch, null,
                        React.createElement(Route, { exact: true, path: "/login", component: Login }),
                        React.createElement(Route, { exact: true, path: "/oidc/callback", component: OidcCallback }),
                        React.createElement(Route, { exact: true, path: "/oidc/callback-silent", component: OidcSilentCallback }),
                        React.createElement(Route, { path: "*", component: Main })))),
            privateState.renderChildren &&
                React.createElement(IdentityProviderAuthenticated, { authenticated: privateState.authenticated, children: children, user: privateState.user })));
    }
    else if (debugMode === true) {
        return React.createElement("h1", null, "configuring IdentityProvider");
    }
    else if (loadingIndicator) {
        return loadingIndicator;
    }
    return null;
}
